import { Component } from 'react';
import { withTranslation } from 'react-i18next';
import { updateProject } from '../../query';
import ReactModal from '../../../../../components/Modal';
import Loading from '../../../../../components/Loading';
import { v4 as uuid } from 'uuid';
import Message from '../../../../Message';
import { createProjects } from '../../../create/query';
import DomainChecker from './DomainChecker';
import MessageCustom from '../../../../../components/MessageCustom';
import ReactSelect from '../../../../../components/ReactSelect';
import ConfirmationModal from '../../../../../components/Modals/ConfirmationModal';
import { COUNTRIES } from '../../../create/countries';
import { LANGUAGES } from '../../../create/languages';

// Renders the exact same card as the modal, minus the react-modal overlay, so the
// project settings page and the edit modal stay visually identical. The `react-modal`
// class is kept on purpose: every light/dark theme rule for the card hangs off it.
function PageWrapper({ className, children }) {
    return <div className={`react-modal as-page ${className ?? ''}`}>{children}</div>;
}

function isValidDomain(domain) {
    const trimed = normalizeDomain(domain);
    const regex = /^(?!:\/\/)([a-zA-Z0-9-_]+\.)+[a-zA-Z]{2,}$/;
    return regex.test(trimed);
}

function normalizeDomain(url) {
    return url
        ?.trim() // remove trailing slash
        .replace(/^https?:\/\//, '') // remove http:// or https://
        .replace(/\/$/, '');
}

function checkDuplicates(own_domain, competitors, clients = []) {
    const domains = competitors.map((item) => normalizeDomain(item.domain));
    clients.forEach((item) => domains.push(normalizeDomain(item.domain)));
    domains.push(normalizeDomain(own_domain));

    const countMap = {};
    const duplicates = new Set();

    for (const domain of domains) {
        countMap[domain] = (countMap[domain] || 0) + 1;
        if (countMap[domain] > 1) {
            duplicates.add(domain);
        }
    }
    return {
        hasDuplicates: duplicates.size > 0,
        duplicates: Array.from(duplicates),
    };
}

class ProjectEditModal extends Component {
    constructor(props) {
        super();
        this.handleChangeOwn = this.handleChangeOwn.bind(this);
        this.handleSelectCountry = this.handleSelectCountry.bind(this);
        this.handleSelectLanguage = this.handleSelectLanguage.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
        this.handleChangeCompetitors = this.handleChangeCompetitors.bind(this);
        this.addCompetitor = this.addCompetitor.bind(this);
        this.deleteCompetitor = this.deleteCompetitor.bind(this);
        this.deleteMessage = this.deleteMessage.bind(this);
        this.validate = this.validate.bind(this);
        this.onHandleSubmit = this.onHandleSubmit.bind(this);
        this.updateCompetitior = this.updateCompetitior.bind(this);
        this.updateOwnDomain = this.updateOwnDomain.bind(this);
        this.handleChangeClients = this.handleChangeClients.bind(this);
        this.addClient = this.addClient.bind(this);
        this.deleteClient = this.deleteClient.bind(this);
        this.updateClient = this.updateClient.bind(this);

        this.onRequestClose = this.onRequestClose.bind(this);
        this.onToggleMarket = this.onToggleMarket.bind(this);
        this.onCancelClose = this.onCancelClose.bind(this);

        this.fromSingleProject = props.fromSingleProject ?? false;
        // Onboarding shows the same form with its non-essential parts put away: a first-time
        // user is answering "who are you and where do you sell", not filling in a project.
        this.onboarding = props.onboarding ?? false;

        const competitors =
            props?.item?.competitors?.map((item) => ({ ...item, id: uuid() })) || [];

        const clients = props?.item?.clients?.map((item) => ({ ...item, id: uuid() })) || [];

        if (props.newCompetitor) {
            if (competitors.length === 10) {
                this.message = props.t('ai_tracking.global.competitors_warning_message');
            } else {
                competitors.push(props.newCompetitor);
            }
        }

        if (props.newClient) {
            if (clients.length === 10) {
                this.message = props.t('ai_tracking.global.clients_warning_message');
            } else {
                clients.push(props.newClient);
            }
        }

        if (props.isCreate) {
            this.state = {
                name: '',
                country: props.defaultMarket?.country ?? '',
                language: props.defaultMarket?.language ?? '',
                own_domain: '',
                own_product: '',
                loading: false,
                isOpendeleteModal: false,
                competitors: [],
                clients: [],
                error: {},
                message: '',
                competitorsErrorMessage: '',
                errors: [],
                isMarketOpen: false,
                isConfirmCloseOpen: false,
            };
        } else {
            this.state = {
                name: props.item.name,
                // A project that has never been given a market takes the caller's guess, so
                // onboarding can profile the brand straight after this step instead of stopping
                // to ask. A market the project already has always wins.
                country: props.item.country || props.defaultMarket?.country || '',
                language: props.item.language || props.defaultMarket?.language || '',
                own_domain: props.item.own_domain ?? '',
                own_product: props.item.own_product_name ?? '',
                loading: false,
                isOpendeleteModal: false,
                competitors: competitors,
                clients: clients,
                error: {},
                message: '',
                competitorsErrorMessage: this.message || '',
                errors: [],
                isMarketOpen: false,
                isConfirmCloseOpen: false,
            };
        }

        // Taken once the state is settled, so a close can tell an untouched form from one the
        // user has put work into.
        this.initialSnapshot = this.snapshot();
    }

    /** Everything the form can change, flattened, for comparing against the opening state. */
    snapshot() {
        const pairs = (items) =>
            (items ?? []).map(({ domain, name }) => ({ domain: domain ?? '', name: name ?? '' }));

        return JSON.stringify({
            name: this.state.name,
            country: this.state.country,
            language: this.state.language,
            own_domain: this.state.own_domain,
            own_product: this.state.own_product,
            competitors: pairs(this.state.competitors),
            clients: pairs(this.state.clients),
        });
    }

    /**
     * Close, asking first if that would throw away typing.
     *
     * Only onboarding asks: elsewhere this form is one of several ways to reach the same
     * project and closing it is a normal thing to do, whereas abandoning step 1 halfway loses
     * the only copy of what was typed.
     */
    onRequestClose() {
        if (this.onboarding && this.snapshot() !== this.initialSnapshot) {
            this.setState({ isConfirmCloseOpen: true });
            return;
        }
        this.props.onClose();
    }

    onCancelClose() {
        this.setState({ isConfirmCloseOpen: false });
    }

    onToggleMarket() {
        this.setState((prevState) => ({ isMarketOpen: !prevState.isMarketOpen }));
    }

    /** The country/language pair, rendered the same wherever it is placed. */
    renderMarketRow(selectControlStyles) {
        const { t } = this.props;

        return (
            <div className="d-flex justify-content-between gap-8 own-product-row">
                <div className="own_product_size">
                    <ReactSelect
                        options={COUNTRIES}
                        value={COUNTRIES.find((item) => item.value === this.state.country) ?? null}
                        label={t('ai_tracking.add_keyword.country')}
                        placeholder={t('ai_tracking.add_keyword.country')}
                        customeOnChange={this.handleSelectCountry}
                        classname="mr-0"
                        disabled={this.state.loading}
                        isSearchable
                        blurInputOnSelect
                        themeStyles={{ borderRadius: 8 }}
                        components={{
                            IndicatorSeparator: () => null,
                        }}
                        controlStyles={selectControlStyles}
                    />
                </div>
                <div className="own_product_size">
                    <ReactSelect
                        options={LANGUAGES}
                        value={LANGUAGES.find((item) => item.value === this.state.language) ?? null}
                        label={t('ai_tracking.add_keyword.language')}
                        placeholder={t('ai_tracking.add_keyword.language')}
                        onChange={this.handleSelectLanguage}
                        classname="mr-0"
                        disabled={this.state.loading}
                        isSearchable
                        blurInputOnSelect
                        themeStyles={{ borderRadius: 8 }}
                        components={{
                            IndicatorSeparator: () => null,
                        }}
                        controlStyles={selectControlStyles}
                    />
                </div>
            </div>
        );
    }

    validate(ownDomain, competitors, clients = []) {
        const { t } = this.props;
        const errors = {};
        if (!this.state.name) {
            errors.name = t('ai_tracking.projects.check_domains.name_required');
        }

        if (!this.state.own_domain) {
            errors.own_domain = t('ai_tracking.projects.check_domains.own_domain_required');
        } else if (this.state.own_domain && !isValidDomain(ownDomain)) {
            errors.own_domain = t('ai_tracking.projects.check_domains.invalid_domain');
        }

        if (!this.state.own_product) {
            errors.own_product = t('ai_tracking.projects.check_domains.own_product_required');
        }

        for (let competitor of competitors) {
            errors.competitors = {
                ...(errors.competitors ? errors.competitors : {}),
                [competitor.id]: {
                    domain: !competitor.domain
                        ? t('ai_tracking.projects.check_domains.competitor_domain_required')
                        : !isValidDomain(competitor.domain)
                        ? t('ai_tracking.projects.check_domains.invalid_domain')
                        : '',
                    name: !competitor.name
                        ? t('ai_tracking.projects.check_domains.competitor_name_required')
                        : '',
                },
            };
        }

        for (let client of clients) {
            errors.clients = {
                ...(errors.clients ? errors.clients : {}),
                [client.id]: {
                    domain: !client.domain
                        ? t('ai_tracking.projects.check_domains.client_domain_required')
                        : !isValidDomain(client.domain)
                        ? t('ai_tracking.projects.check_domains.invalid_domain')
                        : '',
                    name: !client.name
                        ? t('ai_tracking.projects.check_domains.client_name_required')
                        : '',
                },
            };
        }

        const duplicateCheckResult = checkDuplicates(this.state.own_domain, competitors, clients);
        if (duplicateCheckResult.hasDuplicates) {
            errors.duplicates = duplicateCheckResult.duplicates;
        }

        const isAllEmpty =
            !errors.name &&
            !errors.own_domain &&
            !errors.own_product &&
            !errors.duplicates &&
            Object.values(errors?.competitors ?? {}).every((comp) =>
                Object.values(comp).every((v) => v === ''),
            ) &&
            Object.values(errors?.clients ?? {}).every((client) =>
                Object.values(client).every((v) => v === ''),
            );

        if (!isAllEmpty) {
            this.setState({ error: errors });
            return false;
        }

        return true;
    }

    onHandleSubmit(e) {
        e.preventDefault();
        const ownDomain = normalizeDomain(this.state.own_domain);
        const competitors = this.state.competitors?.map((item) => ({
            ...item,
            domain: normalizeDomain(item.domain),
        }));
        const clients = this.state.clients?.map((item) => ({
            ...item,
            domain: normalizeDomain(item.domain),
        }));

        const isValid = this.validate(ownDomain, competitors, clients);
        if (!isValid) return;
        this.onSubmit();
    }

    handleChangeOwn(e) {
        const next = {
            [e.target.name]: e.target.value,
            error: {
                ...this.state.error,
                [e.target.name]: '',
                duplicates: null,
            },
        };

        // While onboarding, the market follows the domain being typed -- a .de domain means
        // Germany, and Germany brings German with it -- right up until the user sets one
        // themselves, after which it is theirs and nothing here overwrites it. A domain whose
        // ending says nothing (.com) leaves whatever was guessed before it alone.
        if (e.target.name === 'own_domain' && this.onboarding && !this.marketTouched) {
            const derived = this.props.deriveMarket?.(e.target.value) ?? {};
            if (derived.country) next.country = derived.country;
            if (derived.language) next.language = derived.language;
        }

        this.setState(next);
    }

    handleSelectCountry(data) {
        this.marketTouched = true;
        this.setState((prevState) => ({
            country: data.value,
            // Every country in COUNTRIES carries its primary language, so picking one
            // fills the language in as well. A handful of entries have no language;
            // those must not wipe a language the user already chose.
            language: data.language || prevState.language,
        }));
    }

    handleSelectLanguage(value) {
        this.marketTouched = true;
        this.setState({ language: value });
    }

    updateOwnDomain(value) {
        this.setState({
            own_domain: value,
        });
    }
    updateCompetitior(id, field, value) {
        this.setState((prevState) => {
            const newCompetitors = [...prevState.competitors];
            const index = newCompetitors.findIndex((competitor) => competitor.id === id);
            newCompetitors[index] = {
                ...newCompetitors[index],
                [field]: value,
            };

            return {
                competitors: newCompetitors,
            };
        });
    }

    updateClient(id, field, value) {
        this.setState((prevState) => {
            const newClients = [...prevState.clients];
            const index = newClients.findIndex((client) => client.id === id);
            newClients[index] = {
                ...newClients[index],
                [field]: value,
            };

            return {
                clients: newClients,
            };
        });
    }

    handleChangeClients(e, id) {
        this.setState((prevState) => {
            const newClients = [...prevState.clients];
            const index = newClients.findIndex((client) => client.id === id);
            let duplicates = prevState?.error?.duplicates ? [...prevState?.error?.duplicates] : [];

            if (e.target.name === 'domain') {
                duplicates = duplicates.filter((domain) => domain !== newClients[index].domain);
            }

            newClients[index] = {
                ...newClients[index],
                [e.target.name]: e.target.value,
            };
            const errorClients = { ...(this.state.error.clients || {}) };
            if (errorClients[id]) {
                errorClients[id][e.target.name] = '';
            }
            return {
                clients: newClients,
                error: {
                    ...this.state.error,
                    clients: errorClients,
                    duplicates: duplicates,
                },
            };
        });
    }

    handleChangeCompetitors(e, id) {
        this.setState((prevState) => {
            const newCompetitors = [...prevState.competitors];
            const index = newCompetitors.findIndex((competitor) => competitor.id === id);
            let duplicates = prevState?.error?.duplicates ? [...prevState?.error?.duplicates] : [];

            if (e.target.name === 'domain') {
                duplicates = duplicates.filter((domain) => domain !== newCompetitors[index].domain);
            }

            newCompetitors[index] = {
                ...newCompetitors[index],
                [e.target.name]: e.target.value,
            };
            const errorCompetitors = { ...(this.state.error.competitors || {}) };
            if (errorCompetitors[id]) {
                errorCompetitors[id][e.target.name] = '';
            }
            return {
                competitors: newCompetitors,
                error: {
                    ...this.state.error,
                    competitors: errorCompetitors,
                    duplicates: duplicates,
                },
            };
        });
    }

    async onSubmit() {
        try {
            let own_domain = normalizeDomain(this.state.own_domain);
            let competitors = this.state.competitors.map((item) => ({
                ...item,
                domain: normalizeDomain(item.domain),
            }));
            let clients = this.state.clients.map((item) => ({
                ...item,
                domain: normalizeDomain(item.domain),
            }));

            this.setState({
                loading: true,
            });

            this.setState({
                loading: true,
            });

            const params = {
                name: this.state.name,
                country: this.state.country || null,
                language: this.state.language || null,
                own_domain: own_domain ?? '',
                own_product_name: this.state.own_product ?? '',
                competitors: competitors.map(({ id, ...rest }) => rest),
                clients: clients.map(({ id, ...rest }) => rest),
            };

            const res = this.props.isCreate
                ? await createProjects(params)
                : await updateProject(this.props.item.id, params);
            const data = await res.json();
            this.setState({
                loading: false,
            });
            if (res.status !== 200 && res.status !== 201) {
                const errors = data?.extra?.errors;
                const errorsForShow = {};
                errors?.forEach((item) => {
                    if (item.source === 'own_domain') {
                        errorsForShow.own_domain = {
                            message: item.message,
                            domain: item.redirection_url || item.domain,
                            isWarning: !!item.redirection_url,
                            isError: !item.redirection_url,
                        };
                        if (item.redirection_url) this.updateOwnDomain(item.redirection_url);
                    } else if (item.source === 'competitor') {
                        const findedCompetitor = this.state.competitors.find(
                            (competitor) =>
                                normalizeDomain(competitor.domain) === normalizeDomain(item.domain),
                        );
                        if (findedCompetitor) {
                            errorsForShow[findedCompetitor.id] = {
                                message: item.message,
                                domain: item.redirection_url || item.domain,
                                isWarning: !!item.redirection_url,
                                isError: !item.redirection_url,
                            };

                            if (item.redirection_url) {
                                this.updateCompetitior(
                                    findedCompetitor.id,
                                    'domain',
                                    item.redirection_url,
                                );
                            }
                        }
                    } else if (item.source === 'client') {
                        const findedClient = this.state.clients.find(
                            (client) =>
                                normalizeDomain(client.domain) === normalizeDomain(item.domain),
                        );
                        if (findedClient) {
                            errorsForShow[findedClient.id] = {
                                message: item.message,
                                domain: item.redirection_url || item.domain,
                                isWarning: !!item.redirection_url,
                                isError: !item.redirection_url,
                            };

                            if (item.redirection_url) {
                                this.updateClient(findedClient.id, 'domain', item.redirection_url);
                            }
                        }
                    }
                });

                this.setState({
                    // message: data?.detail || this.props.t('global.something_went_wrong'),
                    errorsForShow: errorsForShow,
                });

                return;
            }
            this.props.updateData?.('update', this.props?.item?.id, data);
            if (this.props.onSaved) {
                this.props.onSaved(data);
            } else {
                this.props.onClose();
            }
        } catch (err) {
            this.setState({
                loading: false,
            });
        }
    }

    addCompetitor() {
        this.setState({
            competitors: [...this.state.competitors, { domain: '', name: '', id: uuid() }],
        });
    }

    deleteCompetitor(value, key = 'id') {
        let competitors = this.state.competitors.filter((competitor) => competitor[key] !== value);

        if (this.state.competitorsErrorMessage) {
            competitors.push(this.props.newCompetitor);
        }
        this.setState({
            competitors,
            competitorsErrorMessage: '',
        });
    }

    addClient() {
        this.setState({
            clients: [...this.state.clients, { domain: '', name: '', id: uuid() }],
        });
    }

    deleteClient(value, key = 'id') {
        const clients = this.state.clients.filter((client) => client[key] !== value);

        if (this.state.competitorsErrorMessage && this.props.newClient) {
            clients.push(this.props.newClient);
        }
        this.setState({ clients });
    }

    deleteMessage() {
        this.setState({ message: '' });
    }

    render() {
        const { t } = this.props;

        const ownDomainFaild = this.state.errorsForShow?.own_domain;
        const ownClassName = ownDomainFaild?.isError
            ? 'input-error'
            : ownDomainFaild?.isWarning
            ? 'input-warning'
            : '';

        const Wrapper = this.props.asPage ? PageWrapper : ReactModal;

        // A guessed market is shown as a line the user can open; a market we could not guess
        // has to be asked for outright, so the selects start open rather than hiding an empty
        // answer behind a summary that has nothing to summarise.
        const marketCountry = COUNTRIES.find((item) => item.value === this.state.country) ?? null;
        const marketLanguage = LANGUAGES.find((item) => item.value === this.state.language) ?? null;
        const isMarketExpanded = this.state.isMarketOpen || !marketCountry || !marketLanguage;

        // The two selects sit in the same 50% columns as the text inputs beside them,
        // so react-select's default 295px floor has to go or it overflows the card.
        const selectControlStyles = {
            minWidth: 0,
            height: '36px',
            minHeight: '36px',
            color: '#101828',
            fontFamily: 'Inter',
            fontSize: '14px',
            fontStyle: 'normal',
            fontWeight: 500,
            lineHeight: '20px',
        };

        return (
            <>
                <Wrapper
                    modalState={this.props.isOpen}
                    className={`width-sm ai-tracking-modal edit-project ${
                        this.onboarding ? 'onboarding-step' : ''
                    }`}
                    onClose={this.onboarding ? this.onRequestClose : undefined}
                >
                    <h3>
                        {this.props.title ||
                            (this.props.isCreate
                                ? t('ai_tracking.projects.edit.createHeader')
                                : t('ai_tracking.projects.edit.header'))}
                    </h3>
                    {this.props.subtitle && (
                        <p className="as-page-subtitle">{this.props.subtitle}</p>
                    )}
                    <form onSubmit={this.onHandleSubmit}>
                        <div className="ai-tracking-modal-body">
                            {/* Onboarding asks for the brand and nothing else: the project
                                already has the name it was created with, and its market moves
                                to a single line under the form. */}
                            <div
                                className="edit-project-box"
                                style={{ display: this.onboarding ? 'none' : undefined }}
                            >
                                <div className="edit-project-box-title">
                                    <span>{t('ai_tracking.global.project')}</span>
                                    {this.props.asPage && (
                                        <span className="edit-project-box-hint">
                                            {t('ai_tracking.projects.settings.project_hint')}
                                        </span>
                                    )}
                                </div>
                                <div className="mb-10 w-100">
                                    <div className="mb-16">
                                        <label>
                                            {t('ai_tracking.projects.edit.project_name')}{' '}
                                            <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            className="form-control"
                                            type="text"
                                            name="name"
                                            onChange={this.handleChangeOwn}
                                            value={this.state.name}
                                            disabled={this.fromSingleProject || this.state.loading}
                                        />
                                        {this.state.error?.name && (
                                            <span className="error-message">
                                                {this.state.error.name}
                                            </span>
                                        )}
                                    </div>
                                    {this.renderMarketRow(selectControlStyles)}
                                </div>
                            </div>
                            <div className="edit-project-box">
                                <div className="edit-project-box-title">
                                    <span>{t('ai_tracking.global.own_brand')}</span>
                                    {this.props.asPage && (
                                        <span className="edit-project-box-hint">
                                            {t('ai_tracking.projects.settings.own_brand_hint')}
                                        </span>
                                    )}
                                </div>
                                <div className="mb-10 w-100">
                                    <div>
                                        <div className="d-flex justify-content-between gap-8 own-product-row">
                                            <div className="own_product_size">
                                                <label>
                                                    {t('ai_tracking.projects.edit.own_product')}{' '}
                                                    <span className="text-danger">*</span>
                                                </label>
                                                <div>
                                                    <input
                                                        name="own_product"
                                                        className="form-control"
                                                        type="text"
                                                        disabled={this.state.loading}
                                                        onChange={this.handleChangeOwn}
                                                        value={this.state.own_product}
                                                    />
                                                    {this.state.error?.own_product && (
                                                        <span className="error-message">
                                                            {this.state.error.own_product}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="own_product_size">
                                                <label>
                                                    {t('ai_tracking.projects.edit.own_domain')}{' '}
                                                    <span className="text-danger">*</span>
                                                </label>
                                                <div className="input-with-icon">
                                                    <input
                                                        name="own_domain"
                                                        className={`form-control ${ownClassName}`}
                                                        type="text"
                                                        onChange={this.handleChangeOwn}
                                                        value={this.state.own_domain}
                                                        disabled={this.state.loading}
                                                    />
                                                    {ownDomainFaild?.status_code >= 400 ? (
                                                        <img src="/static/img/alert-triangle.svg" />
                                                    ) : (
                                                        <img src="/static/img/globe-02.svg" />
                                                    )}
                                                </div>
                                                {this.state.error?.own_domain && (
                                                    <span className="error-message">
                                                        {this.state.error.own_domain}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        {ownDomainFaild && <DomainChecker item={ownDomainFaild} />}
                                    </div>
                                </div>
                            </div>
                            {this.props.fromSingleProject && (
                                <MessageCustom
                                    message={t(
                                        'ai_tracking.keyword_detail.competitors_refresh_message',
                                    )}
                                    message_type={'alert-info'}
                                    isHeaderSeperated
                                    hideCloseBtn
                                />
                            )}
                            {!this.props.hideCompetitors && (
                                <div className="edit-project-box">
                                    <div className="edit-project-box-title">
                                        <span>{t('ai_tracking.global.competitors')}</span>
                                        {this.props.asPage && (
                                            <span className="edit-project-box-hint">
                                                {t(
                                                    'ai_tracking.projects.settings.competitors_hint',
                                                )}
                                            </span>
                                        )}
                                    </div>
                                    <div className="mb-10 w-100">
                                        <div>
                                            {this.state.competitors?.map((competitor, index) => {
                                                const faildDomain =
                                                    this.state.errorsForShow?.[competitor.id];
                                                const className = faildDomain?.isError
                                                    ? 'input-error'
                                                    : faildDomain?.isWarning
                                                    ? 'input-warning'
                                                    : '';
                                                return (
                                                    <div key={competitor.id}>
                                                        <div className="d-flex justify-content-between align-items-start own-product-row gap-8 mb-16">
                                                            <div className="own_product_size">
                                                                {index === 0 && (
                                                                    <label>
                                                                        {t(
                                                                            'ai_tracking.global.competitor_name',
                                                                        )}{' '}
                                                                        <span className="text-danger">
                                                                            *
                                                                        </span>
                                                                    </label>
                                                                )}
                                                                <div>
                                                                    <input
                                                                        name="name"
                                                                        className="form-control"
                                                                        type="text"
                                                                        placeholder={`Competitor ${
                                                                            index + 1
                                                                        } Brand`}
                                                                        disabled={
                                                                            this.state.loading
                                                                        }
                                                                        onChange={(e) =>
                                                                            this.handleChangeCompetitors(
                                                                                e,
                                                                                competitor.id,
                                                                            )
                                                                        }
                                                                        value={
                                                                            competitor?.name ?? ''
                                                                        }
                                                                    />
                                                                </div>
                                                                {this.state.error?.competitors?.[
                                                                    competitor.id
                                                                ]?.name && (
                                                                    <div className="error-message">
                                                                        {
                                                                            this.state.error
                                                                                .competitors[
                                                                                competitor.id
                                                                            ].name
                                                                        }
                                                                    </div>
                                                                )}{' '}
                                                            </div>
                                                            <div className="own_product_size">
                                                                {index === 0 && (
                                                                    <label>
                                                                        {t(
                                                                            'ai_tracking.global.competitor_domain',
                                                                        )}
                                                                        <span className="text-danger">
                                                                            *
                                                                        </span>
                                                                    </label>
                                                                )}
                                                                <div>
                                                                    <div className="input-with-icon d-flex align-items-center gap-8">
                                                                        <input
                                                                            name="domain"
                                                                            className={`form-control ${className}`}
                                                                            type="text"
                                                                            disabled={
                                                                                this.state.loading
                                                                            }
                                                                            placeholder={`Competitor ${
                                                                                index + 1
                                                                            } Domain`}
                                                                            onChange={(e) =>
                                                                                this.handleChangeCompetitors(
                                                                                    e,
                                                                                    competitor.id,
                                                                                )
                                                                            }
                                                                            value={
                                                                                competitor?.domain ??
                                                                                ''
                                                                            }
                                                                        />
                                                                        {faildDomain?.status_code >=
                                                                        400 ? (
                                                                            <img src="/static/img/alert-triangle.svg" />
                                                                        ) : (
                                                                            <img src="/static/img/globe-02.svg" />
                                                                        )}
                                                                        <button
                                                                            type="button"
                                                                            className="delete-btn"
                                                                            disabled={
                                                                                this.state.loading
                                                                            }
                                                                            onClick={() =>
                                                                                this.deleteCompetitor(
                                                                                    competitor.id,
                                                                                )
                                                                            }
                                                                        >
                                                                            <i className="icon-trash-2" />
                                                                        </button>
                                                                    </div>
                                                                    {this.state.error
                                                                        ?.competitors?.[
                                                                        competitor.id
                                                                    ]?.domain && (
                                                                        <div className="error-message">
                                                                            {
                                                                                this.state.error
                                                                                    .competitors[
                                                                                    competitor.id
                                                                                ].domain
                                                                            }
                                                                        </div>
                                                                    )}{' '}
                                                                    {this.state.error?.duplicates?.includes(
                                                                        competitor?.domain,
                                                                    ) && (
                                                                        <div className="error-message">
                                                                            {t(
                                                                                'ai_tracking.projects.check_domains.duplicate_domain_error',
                                                                            )}
                                                                        </div>
                                                                    )}{' '}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        {faildDomain && (
                                                            <DomainChecker item={faildDomain} />
                                                        )}
                                                    </div>
                                                );
                                            })}
                                            {!this.props.hideCompetitors && (
                                                <button
                                                    className="w-100 button button-outline width-sm btn  d-flex align-items-center justify-content-center"
                                                    type="button"
                                                    disabled={
                                                        this.state.competitors.length >= 10 ||
                                                        this.state.loading
                                                    }
                                                    onClick={this.addCompetitor}
                                                >
                                                    {t(
                                                        'ai_tracking.prompt_generator.add_competitor',
                                                    )}
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                            {!this.props.hideCompetitors && !this.props.hideClients && (
                                <div className="edit-project-box">
                                    <div className="edit-project-box-title">
                                        <span>{t('ai_tracking.global.clients')}</span>
                                        {this.props.asPage && (
                                            <span className="edit-project-box-hint">
                                                {t('ai_tracking.projects.settings.clients_hint')}
                                            </span>
                                        )}
                                    </div>
                                    <div className="mb-10 w-100">
                                        <div>
                                            {this.state.clients?.map((client, index) => {
                                                const faildDomain =
                                                    this.state.errorsForShow?.[client.id];
                                                const className = faildDomain?.isError
                                                    ? 'input-error'
                                                    : faildDomain?.isWarning
                                                    ? 'input-warning'
                                                    : '';
                                                return (
                                                    <div key={client.id}>
                                                        <div className="d-flex justify-content-between align-items-start own-product-row gap-8 mb-16">
                                                            <div className="own_product_size">
                                                                {index === 0 && (
                                                                    <label>
                                                                        {t(
                                                                            'ai_tracking.global.client_name',
                                                                        )}{' '}
                                                                        <span className="text-danger">
                                                                            *
                                                                        </span>
                                                                    </label>
                                                                )}
                                                                <div>
                                                                    <input
                                                                        name="name"
                                                                        className="form-control"
                                                                        type="text"
                                                                        placeholder={`Client ${
                                                                            index + 1
                                                                        } Brand`}
                                                                        disabled={
                                                                            this.state.loading
                                                                        }
                                                                        onChange={(e) =>
                                                                            this.handleChangeClients(
                                                                                e,
                                                                                client.id,
                                                                            )
                                                                        }
                                                                        value={client?.name ?? ''}
                                                                    />
                                                                </div>
                                                                {this.state.error?.clients?.[
                                                                    client.id
                                                                ]?.name && (
                                                                    <div className="error-message">
                                                                        {
                                                                            this.state.error
                                                                                .clients[client.id]
                                                                                .name
                                                                        }
                                                                    </div>
                                                                )}{' '}
                                                            </div>
                                                            <div className="own_product_size">
                                                                {index === 0 && (
                                                                    <label>
                                                                        {t(
                                                                            'ai_tracking.global.client_domain',
                                                                        )}
                                                                        <span className="text-danger">
                                                                            *
                                                                        </span>
                                                                    </label>
                                                                )}
                                                                <div>
                                                                    <div className="input-with-icon d-flex align-items-center gap-8">
                                                                        <input
                                                                            name="domain"
                                                                            className={`form-control ${className}`}
                                                                            type="text"
                                                                            disabled={
                                                                                this.state.loading
                                                                            }
                                                                            placeholder={`Client ${
                                                                                index + 1
                                                                            } Domain`}
                                                                            onChange={(e) =>
                                                                                this.handleChangeClients(
                                                                                    e,
                                                                                    client.id,
                                                                                )
                                                                            }
                                                                            value={
                                                                                client?.domain ?? ''
                                                                            }
                                                                        />
                                                                        {faildDomain?.status_code >=
                                                                        400 ? (
                                                                            <img src="/static/img/alert-triangle.svg" />
                                                                        ) : (
                                                                            <img src="/static/img/globe-02.svg" />
                                                                        )}
                                                                        <button
                                                                            type="button"
                                                                            className="delete-btn"
                                                                            disabled={
                                                                                this.state.loading
                                                                            }
                                                                            onClick={() =>
                                                                                this.deleteClient(
                                                                                    client.id,
                                                                                )
                                                                            }
                                                                        >
                                                                            <i className="icon-trash-2" />
                                                                        </button>
                                                                    </div>
                                                                    {this.state.error?.clients?.[
                                                                        client.id
                                                                    ]?.domain && (
                                                                        <div className="error-message">
                                                                            {
                                                                                this.state.error
                                                                                    .clients[
                                                                                    client.id
                                                                                ].domain
                                                                            }
                                                                        </div>
                                                                    )}{' '}
                                                                    {this.state.error?.duplicates?.includes(
                                                                        client?.domain,
                                                                    ) && (
                                                                        <div className="error-message">
                                                                            {t(
                                                                                'ai_tracking.projects.check_domains.duplicate_domain_error',
                                                                            )}
                                                                        </div>
                                                                    )}{' '}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        {faildDomain && (
                                                            <DomainChecker item={faildDomain} />
                                                        )}
                                                    </div>
                                                );
                                            })}
                                            <button
                                                className="w-100 button button-outline width-sm btn  d-flex align-items-center justify-content-center"
                                                type="button"
                                                disabled={
                                                    this.state.clients.length >= 10 ||
                                                    this.state.loading
                                                }
                                                onClick={this.addClient}
                                            >
                                                {t('ai_tracking.prompt_generator.add_client')}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {this.onboarding && (
                                <div className="edit-project-box onboarding-market-box">
                                    {isMarketExpanded ? (
                                        <>
                                            <div className="edit-project-box-title">
                                                <span>
                                                    {t('ai_tracking.onboarding.market.header')}
                                                </span>
                                                <span className="edit-project-box-hint">
                                                    {t('ai_tracking.onboarding.market.hint')}
                                                </span>
                                            </div>
                                            <div className="mb-10 w-100">
                                                {this.renderMarketRow(selectControlStyles)}
                                            </div>
                                        </>
                                    ) : (
                                        <div className="onboarding-market-summary">
                                            <span>
                                                {t('ai_tracking.onboarding.market.summary', {
                                                    country: marketCountry.label,
                                                    language: marketLanguage.label,
                                                })}
                                            </span>
                                            <button
                                                type="button"
                                                className="onboarding-market-change"
                                                onClick={this.onToggleMarket}
                                            >
                                                {t('ai_tracking.onboarding.market.change')}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                            {this.state.competitorsErrorMessage && (
                                <div className="competitors-error-message">
                                    <img src="/static/img/alert-icon.svg" alt="icon" />
                                    {this.state.competitorsErrorMessage}
                                </div>
                            )}
                        </div>
                        <div className="ai-tracking-modal-footer">
                            <button
                                className="button button-filled button-primary width-sm btn  d-flex gap-5 align-items-center justify-content-center"
                                disabled={this.state.loading}
                                style={{ lineHeight: 'unset', marginRight: '10px' }}
                            >
                                {this.state.loading && <Loading onlyPreloader />}
                                {this.props.submitLabel ||
                                    (this.props.isCreate
                                        ? t('ai_tracking.projects.edit.create')
                                        : t('ai_tracking.projects.edit.save'))}
                            </button>
                            <button
                                type="button"
                                className="button button-outline button-primary width-sm btn"
                                onClick={this.onRequestClose}
                                style={{ lineHeight: 'unset' }}
                            >
                                {this.props.closeLabel || t('ai_tracking.projects.edit.close')}
                            </button>
                        </div>
                    </form>
                    {/* )} */}
                </Wrapper>
                {this.state.isConfirmCloseOpen && (
                    <ConfirmationModal
                        isOpen
                        message={t('ai_tracking.onboarding.discard.message')}
                        buttonName={t('ai_tracking.onboarding.discard.confirm')}
                        cancleBtn={t('ai_tracking.onboarding.discard.cancel')}
                        onClose={this.onCancelClose}
                        onSubmit={this.props.onClose}
                    />
                )}
                {this.state.message && (
                    <div className="messages">
                        <Message
                            message_tags={'error'}
                            message={this.state.message}
                            deleteMessage={this.deleteMessage}
                        />
                    </div>
                )}
            </>
        );
    }
}

export default withTranslation()(ProjectEditModal);
